import { useBehavior, useButton, useTriggerAction } from '@libs/core';
import { TButtonData, TWidget, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { TPromoStoriesData } from '../../types/data';
import { TPromoStoriesOption } from '../../types/options';
import { capitalText, getInlineStyles } from '../../utils/styles';
import { ButtonPillView } from '../Buttons/ButtonPillView';
import { NavBar } from '../UI/NavBar/NavBar';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './promoStories.module.css';
import { Stories } from './Stories';
import { getBg, getBgImage, TImageEnum } from './utils';

enum ActionNameEnum {
    onTopLeftButtonClick = 'onTopLeftButtonClick',
    onTopRightButtonClick = 'onTopRightButtonClick',
    onBottomLeftButtonClick = 'onBottomLeftButtonClick',
    onBottomRightButtonClick = 'onBottomRightButtonClick',
    onSlideButtonClick = 'onSlideButtonClick',
}

export const PromoStories = ({ widget }: TWidgetProps<TPromoStoriesData, TPromoStoriesOption>) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const slides = data.slides ?? [];
    const { isDisabled } = useBehavior(widget as unknown as TWidget<TButtonData>);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget as unknown as TWidget<TButtonData>);
    const { handleClickByActionName } = useButton(widget as unknown as TWidget<TButtonData>, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });

    const justifyCenter = options.bottomLeftButtonVisible && options.bottomRightButtonVisible;
    const storyBottomButtonsWrapper = clsx(styles.bottomButtonsWrapper, justifyCenter && styles.justifyCenter);
    const topButtonRightWrappersClass = clsx(styles.topButtonWrapper, styles.topButtonWrapperRight);
    const topButtonLeftWrappersClass = clsx(styles.topButtonWrapper, styles.topButtonWrapperLeft);

    const getSteps = () => {
        return slides.map((slide) => {
            const contentPosition = slide.contentPosition;
            const isFillContainer = options.bottomLeftButtonVisible && options.bottomRightButtonVisible;
            const storyImgClasses = clsx(styles.storyImg);
            const storyInfoClasses = clsx(styles.storyInfo, styles[`storyInfo${capitalText(contentPosition)}`]);
            const storyContentClasses = clsx(styles.storyContent, styles.animatedContent, styles[`storyContent${capitalText(contentPosition)}`]);
            const storyContainerClasses = clsx(styles.storyContainer, slide.fullwidthImage && styles.fullWidthImage);

            const createSrcSet = (image1x?: string, image2x?: string, image3x?: string) => {
                return [image1x && `${image1x} 1x`, image2x && `${image2x} 2x`, image3x && `${image3x} 3x`].filter(Boolean).join(', ');
            };

            const srcSetA = createSrcSet(slide.images?.bgImageA, slide.images?.bgImageA2x, slide.images?.bgImageA3x);
            const srcSetB = createSrcSet(slide.images?.bgImageB, slide.images?.bgImageB2x, slide.images?.bgImageB3x);

            return (
                <div
                    className={storyContainerClasses}
                    key={slide.title}
                    style={getInlineStyles({
                        variables: {
                            backgroundA: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.A, false)),
                            backgroundAx2: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.A, true)),
                            backgroundAx3: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.A, false, true)),
                            backgroundB: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.B, false)),
                            backgroundBx2: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.B, true)),
                            backgroundBx3: getBg(slide, getBgImage(slide.images ?? {}, TImageEnum.B, false, true)),
                        },
                    })}
                >
                    <NavBar align={'space-between'} insideStories>
                        <div className={topButtonLeftWrappersClass}>
                            {options.topLeftButtonVisible && (
                                <ButtonPillView
                                    label={data.messages?.topLeftButtonLabel}
                                    hierarchy={options.topLeftButtonHierarchy}
                                    viewBackground={'gradient'}
                                    size={options.topLeftButtonSize}
                                    iconName={data.topLeftButtonIconName}
                                    flagIcon={data.topLeftButtonFlagIcon}
                                    onClick={() => handleClickByActionName(ActionNameEnum.onTopLeftButtonClick)}
                                />
                            )}
                        </div>
                        <div className={topButtonRightWrappersClass}>
                            {options.topRightButtonVisible && (
                                <ButtonPillView
                                    label={data.messages?.topRightButtonLabel}
                                    hierarchy={options?.topRightButtonHierarchy}
                                    viewBackground={'gradient'}
                                    size={options?.topRightButtonSize}
                                    iconName={data.topRightButtonIconName}
                                    flagIcon={data.topRightButtonFlagIcon}
                                    onClick={() => handleClickByActionName(ActionNameEnum.onTopRightButtonClick)}
                                />
                            )}
                        </div>
                    </NavBar>
                    <div className={storyContentClasses}>
                        <div className={storyInfoClasses}>
                            <TextCommon
                                isWidget={false}
                                content={slide.title}
                                options={{
                                    variant: 'display2',
                                    textColor: 'white_base',
                                    textAlign: 'center',
                                    enableAutoResize: true,
                                    maxLines: 4,
                                }}
                            />
                            <TextCommon
                                isWidget={false}
                                content={slide.subtitle}
                                options={{
                                    variant: 'main',
                                    textColor: 'white_72_tr_base',
                                    textAlign: 'center',
                                    enableAutoResize: true,
                                    maxLines: 5,
                                }}
                            />
                            {slide.buttonVisible && (
                                <div className={styles.storyContentButton}>
                                    <ButtonPillView
                                        label={slide.buttonText}
                                        iconName={slide.buttonIconName}
                                        hierarchy={'tertiary'}
                                        viewBackground={'gradient'}
                                        size={'medium'}
                                        onClick={() => handleClickByActionName(ActionNameEnum.onSlideButtonClick)}
                                    />
                                </div>
                            )}
                        </div>
                        <picture className={storyImgClasses}>
                            {!slide.fullwidthImage && (
                                <>
                                    {srcSetA && <source media="(max-width: 480px)" srcSet={srcSetA} />}
                                    <img src={getBgImage(slide.images ?? {}, TImageEnum.B)} srcSet={srcSetB} alt={slide.title ?? 'Story image'} />
                                </>
                            )}
                        </picture>
                    </div>
                    <div className={styles.storyBottomButtons}>
                        <div className={storyBottomButtonsWrapper}>
                            {options?.bottomLeftButtonVisible && (
                                <ButtonPillView
                                    label={data.messages?.bottomLeftButtonLabel}
                                    hierarchy={options?.bottomLeftButtonHierarchy}
                                    customBackground={options?.bottomLeftButtonHierarchy === 'secondary' ? 'white_base' : undefined}
                                    size={options?.bottomLeftButtonSize}
                                    fillContainer={isFillContainer}
                                    maxWidth={'208px'}
                                    onClick={() => handleClickByActionName(ActionNameEnum.onBottomLeftButtonClick)}
                                />
                            )}
                            {options?.bottomRightButtonVisible && (
                                <ButtonPillView
                                    label={data.messages?.bottomRightButtonLabel}
                                    hierarchy={options?.bottomRightButtonHierarchy}
                                    customBackground={options?.bottomRightButtonHierarchy === 'secondary' ? 'white_base' : undefined}
                                    size={options?.bottomRightButtonSize}
                                    fillContainer={isFillContainer}
                                    maxWidth={'208px'}
                                    onClick={() => handleClickByActionName(ActionNameEnum.onBottomRightButtonClick)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            );
        });
    };

    return <Stories uid={widget.uid} widgetOptions={options} infinityLoop isInverted duration={data.duration} steps={getSteps()} />;
};
